/* eslint-disable no-console */
import { inject, OnDestroy, OnInit } from "@angular/core";
import { Component } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DatabaseService } from "./servicios/database/database.service";
import { DrawService } from "./servicios/map/draw.service";
import { MessageService } from "src/app/servicios/message/message.service";
import { BnNgIdleService } from "bn-ng-idle";
import { FsessionService } from "./servicios/session/fsession.service";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AuthenticationResult, EventMessage } from "@azure/msal-browser";
import { filter, map, tap } from "rxjs/operators";
import { MenuHeader } from "./component/header/header.component";
import { ErrorsB2C } from "./models";

import { MAT_DATE_LOCALE } from "@angular/material/core";

import moment from 'moment';
import { MunicipalityTokenService } from "./servicios/municipality-token/municipality-token.service";
import { TokenRefreshService } from './servicios/municipality-token/token-refresh.service';
import { printDebug } from "./utils/debugger/app-debugger";
import { updateTokensAndRefresh } from "./servicios/municipality-token/token-utils";
import { InitialDefaultRouteService } from "./servicios/default-route/initial-default-route.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    providers: [
        {
            provide: MAT_DATE_LOCALE,
            deps: [TranslateService],
            useFactory: (translateService: TranslateService) => translateService.currentLang
        }
    ],
     // Store session before tab refresh or unload
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private db = inject(DatabaseService);
  private bnIdle = inject(BnNgIdleService);
  private router = inject(Router);
  private fse = inject(FsessionService);
  private messageService = inject(MessageService);
  private draw = inject(DrawService);
  private translate = inject(TranslateService);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private tokenRefreshService = inject(TokenRefreshService);

  title = "plantilla";
  keytranslate: string[] = ['error_empty_email'];
  variables: any;
  defaultMunicipality: { modulos: any; rol?: any; permiso?: any; id?: any; idusuario?: any; };
  municipalityTokenService = inject(MunicipalityTokenService);
  private initialDefaultRouteService = inject(InitialDefaultRouteService);
  loaded = false;

  constructor() {
    this.restorePassAndChangesProfile();
    this.translate.get(this.keytranslate).subscribe(variables=> this.variables = variables); 

    this.translate.onLangChange.subscribe((event) => {
      this.fse.agregarActualizar('language', event.lang);
      moment.locale(event.lang);
    });

    // Refresca el token proactivamente
    this.tokenRefreshService.start();
  }
  
  async ngOnInit() {
    // Establece la ruta de inicio en el storage, esto para que no se pierda la ruta al redirecionar por ejemplo al 'login'
    if ( window.location.pathname !== '/' && window.location.pathname !== '/auth' ) {
      this.fse.agregarActualizar( 'routeInit', window.location.pathname );
    }

    /** 
     * Valida si un usuario tiene la sesión de azure inciada y esta intentado ir a la ruta "auth#state"  
     * Alli no deberia ir ya que tiene la sesión abierta por ende llamamos el metodo "setRoutes" para establecer las rutas
    */
     if ( this.msalService?.instance?.getAllAccounts().length > 0 
          && window.location.href.includes('auth#state') 
          && this.router.url === '/' ) 
    {
      await this.setupSessionFromActiveAccount();
    }

    this.db.asignarIpPublica();
    this.draw.colorcarVariables();

    this.bnIdle
      .startWatching(environment.time_inactivity_ms)
      .subscribe((res) => {
        // Cierra session por tiempo de inactividad
        if (res && this.fse.optenerValor("dato1") != false) {
          this.translate.get("inactividad").subscribe((message) => {
            this.messageService.SwalMessage("error", message).then(() => {
              this.db.Logout().subscribe(() => {
                this.fse.closeSession();
                this.router.navigate(["login"]);
              });
            });
          });
        }
      });

    this.loaded = false;

    this.getData();

  }
  
  /**
   * Obtener datos del usuario
   */
  getData(){
    this.msalBroadcastService.msalSubject$
      .pipe(
        tap((eventMessage: EventMessage)=>{

          if( eventMessage.eventType == "msal:loginFailure" ){
            let error = eventMessage.error.message.split(' ');
            let errorB2c = error[1].toString().slice(0, -1);
            this.fse.agregarActualizar('errorB2C', errorB2c);
          } else {
            if ( eventMessage.error !== null ) {
              const errorB2c = eventMessage.error.message.split(' ')[1].toString().slice(0, -1);
              if ( errorB2c === ErrorsB2C["B2C-User-Email-Invalid"] ) this.fse.agregarActualizar('errorB2C', errorB2c);
            }
          }

          if (eventMessage.eventType === 'msal:initializeEnd') {
            this.loaded = true;
          }

        }),
        filter(
          (eventMessage: EventMessage) =>
            eventMessage.eventType === "msal:loginSuccess"
        ),
        map((eventMessage: EventMessage) => eventMessage.payload)
      )
      .subscribe({
        next: async (response: AuthenticationResult) => {
          this.msalService.instance.setActiveAccount(response.account);
          this.loaded = true;

          let {
           extension_fvru,
           extension_lang,
           extension_fviu,
           extension_fvpu,
           extension_fvmod,
           extension_f_cad_dev,
           extension_f_cad_prev,
           extension_default_municipality
          } = response.idTokenClaims as any
          
          const rol = extension_fvru;
          const permiso = extension_fvpu;
          const modulos = extension_fvmod;
          const caducidad_dev = String(extension_f_cad_dev ?? "");
          const caducidad_preview = String(extension_f_cad_prev ?? "");

          this.defaultMunicipality = {
            id: extension_default_municipality,
            idusuario: extension_fviu,
            rol: rol,
            permiso: permiso,
            modulos: modulos.toString(),
          }
          
          this.fse.agregarActualizar('fecha_caducidad_dev', caducidad_dev);
          this.fse.agregarActualizar('fecha_caducidad_preview', caducidad_preview);
          
          this.fse.setRole(rol);
          
          if (extension_lang) {
            this.fse.agregarActualizar('language', extension_lang);
          }
          
          if (response.account.idTokenClaims?.emails && response.account.idTokenClaims.emails[0]) {
            this.fse.agregarActualizar('dato48', response.account.idTokenClaims.emails[0].toString());
          } else {
            this.messageService.SwalMessage('error', this.variables.error_empty_email);
            return;
          }

          printDebug(`INITIALIZING AUTHENTICATION: getData() msal:loginSuccess`, 'getData', 'info')

          const tokenReady = await this.getDefaultMunicipalityToken();

          if (tokenReady) {
            this.setRoutes();
          }
        },
        error: () => {
        },
      });
  }
  
  private async setupSessionFromActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0];

    if (!activeAccount?.idTokenClaims) {
      return;
    }
  
    const idTokenClaims = activeAccount.idTokenClaims as any;
    const extension_default_municipality = idTokenClaims.extension_default_municipality;
    const extension_fviu = idTokenClaims.extension_fviu;
    const extension_fvru = idTokenClaims.extension_fvru;
    const extension_fvpu = idTokenClaims.extension_fvpu;
    const extension_fvmod = idTokenClaims.extension_fvmod;

    if (!extension_default_municipality || !extension_fvmod) {
      printDebug(`NO DEFAULT MUNICIPALITY OR MODULES`, 'setupSessionFromActiveAccount', 'info');
      printDebug(JSON.stringify(idTokenClaims), 'setupSessionFromActiveAccount', 'info');

      return;
    }

    this.defaultMunicipality = {
      id: extension_default_municipality,
      idusuario: extension_fviu,
      rol: extension_fvru,
      permiso: extension_fvpu,
      modulos: extension_fvmod.toString(),
    };

    const tokenReady = await this.getDefaultMunicipalityToken();
    if (tokenReady) {
      this.setRoutes();
    }
  }

  getDefaultMunicipalityToken(): Promise<boolean> {
    return new Promise((resolve) => {
      this.db.getModulosPermisosRol(null, this.defaultMunicipality.id).subscribe({
        next: ({ accessToken, refreshToken }) => {
          printDebug(`GETTING MUNICIPALITY TOKEN`, 'getDefaultMunicipalityToken', 'info')
          
          Promise.all([
            this.municipalityTokenService.verifyAndStoreToken(accessToken),
            this.municipalityTokenService.verifyToken(refreshToken),
          ]).then(() => {
            updateTokensAndRefresh(
              this.fse,
              this.municipalityTokenService,
              this.tokenRefreshService,
              accessToken,
              refreshToken,
              'start'
            );
            printDebug(`GETTING MUNICIPALITY TOKEN: SUCCESS`, 'getDefaultMunicipalityToken', 'info');
            resolve(true);
          }).catch((error) => {
            printDebug(`GETTING MUNICIPALITY TOKEN: FAILURE ${error}`, 'getDefaultMunicipalityToken', 'error');
            resolve(false);
          });
        },
        error: (error) => {
          printDebug(`GETTING MUNICIPALITY TOKEN: FAILURE ${error}`, 'getDefaultMunicipalityToken', 'error');
          resolve(false);
        }
      });
    });
  }

  /**
   * Validar rutas
   */
  async setRoutes(){
    const navigated = await this.initialDefaultRouteService.tryNavigateToDefaultRoute();
    if (navigated) {
      return;
    }

    const headers: MenuHeader[] = [
      { routerLink: 'traffic',      type: 1,  name: 'Trafico',        permission: this.defaultMunicipality.modulos },
      { routerLink: 'management',   type: 2,  name: 'Gestion',        permission: this.defaultMunicipality.modulos },
      { routerLink: 'telemetry',    type: 4,  name: 'Telemetría',     permission: this.defaultMunicipality.modulos },
      { routerLink: 'zar',          type: 5,  name: 'infracciones',   permission: this.defaultMunicipality.modulos },
      { routerLink: 'weather',      type: 6,  name: 'Clima',          permission: this.defaultMunicipality.modulos },
      { routerLink: 'red-light',    type: 10, name: 'red-light',      permission: this.defaultMunicipality.modulos },
      { routerLink: 'waste',        type: 11, name: 'Residuos',       permission: this.defaultMunicipality.modulos },
      { routerLink: 'crosswalk',    type: 12, name: 'crosswalk',      permission: this.defaultMunicipality.modulos },
    ];

    const firstModule = headers.find((res)=>{
      let permission = res.permission;
      (permission as any) = permission.toString().split(',').includes(res.type.toString());
      return (permission as any) == true;
    });

    printDebug(`SETTING ROUTES: ${firstModule.name}, ${firstModule.routerLink}, ${firstModule.permission}`, 'setRoutes', 'info');

    this.fse.agregarActualizar('defaulRoute', firstModule.routerLink);
    this.router.navigate([firstModule.routerLink]);
  }

  /**
   * Funcion para validar si se realizo un restaurar contraseña o un cambio en los datos de perfil
   */
  restorePassAndChangesProfile(){
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        // Verificar si se existes las variables
        if(result){
          if(result.account.idTokenClaims){
            const res = result.account.idTokenClaims;
            const fviu = res.extension_fviu;
            const fvmod = res.extension_fvmod;
            const fvpu = res.extension_fvpu;
            const fvru = res.extension_fvru;
            const fvuser = res.extension_fvuser;
  
            if(!fviu || !fvmod || !fvpu || !fvru || !fvuser){
              this.fse.agregarActualizar('restorePass', 'true');
              this.msalService.logout();
              this.router.navigate(['/'], { replaceUrl: true });
            }
          }
        }
      }
    });
  }
  
  ngOnDestroy(): void {
    this.fse.storeSession();
  }

  handleBeforeUnload(): void {
    this.fse.storeSession();
  }

}

